import { Link, useSearchParams } from 'react-router-dom';
import { useMemo, useState } from 'react';

import useFetch from '../../hooks/useFetch';
import { getCategories, getProducts } from '../../services/api';
import ProductImage from '../../components/ProductImage';
import CategoryIcon from '../../components/CategoryIcon';
import Loader from '../../components/Loader';
import ErrorState from '../../components/ErrorState';

/* Map badge field → display label. When badge is 'None', fall back to stock_status label. */
function getBadgeLabel(badge, stockStatus) {
  switch (badge) {
    case 'BestSeller':  return 'Best Seller';
    case 'LowBSR':      return 'Low BSR';
    case 'NewArrival':  return 'New Arrival';
    case 'Clearance':   return 'Clearance';
    default:
      switch (stockStatus) {
        case 'InStock':       return 'In Stock';
        case 'LowStock':      return 'Low Stock';
        case 'OutOfStock':    return 'Out of Stock';
        case 'Discontinued':  return 'Discontinued';
        default:              return null;
      }
  }
}

function ProductCard({ product }) {
  const primary = product.images?.find((i) => i.is_primary) ?? product.images?.[0] ?? null;
  const media = primary?.media ?? null;
  const emoji = product.category?.icon ?? '📦';
  const badgeLabel = getBadgeLabel(product.badge, product.stock_status);

  return (
    <Link to={`/products/${product.slug}`} className="card">
      <div className="card-img">
        {badgeLabel && <span className="card-tag">{badgeLabel}</span>}
        <ProductImage media={media} emoji={emoji} alt={product.name} />
      </div>
      <div className="card-body">
        <div className="card-cat">{product.category?.name}</div>
        <div className="card-name">{product.name}</div>
        <div className="card-brand">{product.brand}</div>
        <div className="card-foot">
          <span className="card-price">
            ${Number(product.wholesale_price).toFixed(2)}{' '}
            <small>/unit</small>
          </span>
          <span className="card-view">View →</span>
        </div>
      </div>
    </Link>
  );
}

function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get('cat') || 'all';

  /* Fetch categories for chip bar and to resolve category name */
  const { data: categories, loading: catsLoading } = useFetch(
    () => getCategories(),
    []
  );

  /* Fetch products filtered by category slug (or all when cat === 'all').
     Public list defaults to take=10; request a large page so the full catalog
     shows (the mockup has no pagination). */
  const { data: products, loading: prodsLoading, error: prodsError } = useFetch(
    () =>
      getProducts(
        cat && cat !== 'all'
          ? { category: cat, take: 100 }
          : { take: 100 }
      ),
    [cat]
  );

  /* Sort selection: '' = default (API order), 'price_asc', 'price_desc' */
  const [sort, setSort] = useState('');

  /* Apply client-side sorting on the fetched products */
  const sortedProducts = useMemo(() => {
    if (!products) return products;
    if (sort === 'price_asc' || sort === 'price_desc') {
      const dir = sort === 'price_asc' ? 1 : -1;
      return [...products].sort(
        (a, b) =>
          (Number(a.wholesale_price) - Number(b.wholesale_price)) * dir
      );
    }
    return products;
  }, [products, sort]);

  /* Resolve selected category name for the h1 */
  const selectedCategoryName = useMemo(() => {
    if (!categories || cat === 'all') return null;
    const found = categories.find((c) => c.slug === cat);
    return found ? found.name : null;
  }, [categories, cat]);

  const headTitle = selectedCategoryName || 'Wholesale Products';

  function handleChipClick(slug) {
    if (slug === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ cat: slug });
    }
  }

  const loading = catsLoading || prodsLoading;

  return (
    <main>
      {/* PAGE HEAD */}
      <div className="page-head">
        <div className="page-head-inner">
          <p className="label">Product Catalog</p>
          <h1>{headTitle}</h1>
          <p>Browse top-performing, brand-authorized SKUs across major Amazon categories.</p>
        </div>
      </div>

      <div className="products-wrap">
        {/* CATEGORY FILTER CHIPS */}
        <div className="filters">
          <button
            className={`chip${cat === 'all' ? ' active' : ''}`}
            onClick={() => handleChipClick('all')}
          >
            All Products
          </button>
          {(categories || []).map((category) => (
            <button
              key={category.id}
              className={`chip${cat === category.slug ? ' active' : ''}`}
              onClick={() => handleChipClick(category.slug)}
            >
              {category.icon && (
                <CategoryIcon icon={category.icon} imgClassName="chip-icon-img" />
              )}
              {category.name}
            </button>
          ))}
        </div>

        {/* RESULT BAR */}
        {!loading && !prodsError && (
          <div className="result-bar">
            <div className="result-count">
              <b>{products ? products.length : 0}</b> products
            </div>
            <div className="sort">
              <select value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="">Sort by</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
              </select>
            </div>
          </div>
        )}

        {/* STATES */}
        {loading && <Loader />}

        {!loading && prodsError && (
          <ErrorState message="Failed to load products. Please try again later." />
        )}

        {/* PRODUCT GRID */}
        {!loading && !prodsError && sortedProducts && sortedProducts.length > 0 && (
          <div className="products-grid">
            {sortedProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* EMPTY STATE */}
        {!loading && !prodsError && products && products.length === 0 && (
          <div className="empty">No products found in this category yet.</div>
        )}
      </div>
    </main>
  );
}

export default Products;
